import { createTranslationService } from '@services/factories';
import { IDeclineModalStore } from '@stores/modal/decline-modal-store';
import { cacheTranslations } from '@utils/cache-translations';
import {
    action,
    computed,
    observable
} from 'mobx';
import { Field } from '@models/field-builder/field';
import { FieldType } from '@models/field-builder/field-type';
import { LabelPosition } from '@models/field-builder/label-position';
import { IRemoteRepresentativeService } from '@services/remote-representative-service';
import { ReviewSection3Step } from '@models/remote-representative/review-section3-step';
import {
    RemoteI9Details
} from '@dtos/remote-i9-details';
import { RepresentativeSection3SignRequest } from '@dtos/remote-i9-sign';
import type { RepresentativeDeclineRequest } from '@dtos/remote-i9-decline';
import { DeclineReason } from '@dtos/remote-i9-decline';
import CipherService from '@services/cipher-service';
import { IConfigurationValuesService } from '@services/ConfigurationValuesService';
import { ITrackingService } from '@services/TrackingService';
import { ClientTrackingCategory } from '@models/client-tracking-category';
import { ClientTrackingName } from '@models/client-tracking-name';
import moment from 'moment';
import { defaultIfEmpty } from '@utils/string-utils';
import { EncryptedDto } from '@dtos/encrypted-dto';
import { I9DocumentInfoDto } from '@dtos/I9Form';

export interface IRemoteRepresentativeStore {
    readonly currentStep: ReviewSection3Step;
    readonly section3Fields: Field[];
    readonly remoteI9Details: RemoteI9Details | null;
    readonly eSignAgreement: boolean;
    readonly representativeName: string | undefined;
    readonly hadErrorESigning: boolean;
    readonly eSignIsFilled: boolean;
    readonly documentTypesValidationsAll: I9DocumentInfoDto[];
    initialise(): Promise<void>;
    goToReviewStep(): void;
    goToCompletionStep(): void;
    requestESignAsync(): Promise<void>;
    requestDeclineAsync(): Promise<void>;
    setESignAgreement(value: boolean): void;
    setEmployerName(value: string): void;
    setDeclineReason(value: string): void;
    setDeclineReasonText(value: string): void;
    updateWithDecryptedDocumentNumber(encryptedDocument: EncryptedDto): string;
    closeDeclineModal(): void;
}

enum RemoteRepresentativeStoreTranslation {
    naLabel = 'naLabel',
    documentPhysicallyExamined = 'documentPhysicallyExamined',
    agreeToSign = 'agreeToSign',
    reasonLabel = 'reasonLabel',
    selectLabel = 'selectLabel',
    declineReasonCannotExamineDoc = 'declineReasonCannotExamineDoc',
    declineReasonIllegalDoc = 'declineReasonIllegalDoc',
    declineReasonInvalidDocument = 'declineReasonInvalidDocument',
    declineReasonIncorrectInfo = 'declineReasonIncorrectInfo',
    declineReasonNotComfortableSigning = 'declineReasonNotComfortableSigning',
    declineReasonIllegalRepresentative = 'declineReasonIllegalRepresentative',
    declineReasonOther = 'declineReasonOther',
    todaysDateError = 'todaysDateError'
}

export enum Section3DeclineField {
    SelectDeclineReason,
    DeclineReasonText
}

class RemoteRepresentativeStore implements IRemoteRepresentativeStore {

    private translations = cacheTranslations(
        createTranslationService(),
        RemoteRepresentativeStoreTranslation
    );

    @observable private selectedStep: ReviewSection3Step = ReviewSection3Step.REVIEW;
    @observable private remoteI9Info: RemoteI9Details | null = null;

    // section3 checkboxes labels values
    @observable private newNameLastNameLabel: string = this.translations.naLabel;
    @observable private newNameFirstNameLabel: string = this.translations.naLabel;
    @observable private newNameMiddleInitialLabel: string = this.translations.naLabel;
    @observable private rehireDateLabel: string = this.translations.naLabel;
    @observable private documentTitleExaminedLabel: string = this.translations.documentPhysicallyExamined;
    @observable private documentNumberExaminedLabel: string = this.translations.documentPhysicallyExamined;
    @observable private expirationDateExaminedLabel: string = this.translations.documentPhysicallyExamined;
    @observable private agreeToSignLabel: string = this.translations.agreeToSign;

    // section3 checkboxes values
    @observable private newNameLastName: boolean = false;
    @observable private newNameFirstName: boolean = false;
    @observable private newNameMiddleInitial: boolean = false;
    @observable private rehireDate: boolean = false;
    @observable private documentTitleExamined: boolean = false;
    @observable private documentNumberExamined: boolean = false;
    @observable private expirationDateExamined: boolean = false;
    @observable private agreeToSign: boolean = false;
    @observable private todaysDate: string = '';
    @observable private representativeNameValue: string = '';
    @observable private eSignAgreementValue: boolean = false;

    // display modals
    @observable showLoader: boolean = false;

    // validations
    @observable hadErrorDeclining: boolean = false;
    @observable hadErrorESigning: boolean = false;
    @observable eSignIsFilledValue: boolean = true;
    @observable declineIsFilledValue: boolean = true;
    @observable todaysDateHasError: boolean = false;
    @observable todaysDateInvalidMessage = '';

    @observable
    documentTypesValidationsAll: I9DocumentInfoDto[] = [];

    constructor(
        private remoteRepresentativeService: IRemoteRepresentativeService,
        private configurationValuesService: IConfigurationValuesService,
        private trackingService: ITrackingService,
        private declineModalStore: IDeclineModalStore
    ) {
    }

    async initialise() {
        await this.getRemoteI9Details();
    };

    private checkESignFilled(): boolean {
        const allFieldsChecked = this.section3Fields
            .filter(field => field.type === FieldType.CHECKBOX)
            .every(prop => prop.value);

        return allFieldsChecked &&
            this.checkTodaysDate(this.todaysDate) &&
            !!this.eSignAgreementValue &&
            !!this.representativeNameValue;
    }
    private checkTodaysDate(date: string): boolean {
        this.todaysDateHasError = false;
        this.todaysDateInvalidMessage = '';
        const isToday = moment().isSame(date, 'd')
        if (!isToday) {
            this.todaysDateHasError = true;
            this.todaysDateInvalidMessage = this.translations.todaysDateError;
            return false;
        }
        return true;
    }

    @computed
    get remoteI9Details(): RemoteI9Details | null {
        return this.remoteI9Info;
    }

    @computed
    get currentStep() {
        return this.selectedStep;
    }

    @action.bound
    closeDeclineModal() {
        this.declineModalStore.closeModal();
    }

    @computed
    get eSignIsFilled(): boolean {
        return this.eSignIsFilledValue;
    }

    @computed
    get eSignAgreement(): boolean {
        return this.eSignAgreementValue;
    }

    @computed
    get representativeName() {
        return this.representativeNameValue;
    }

    @computed
    get section3Fields(): Field[] {
        return [
            {
                type: FieldType.CHECKBOX,
                id: 'new-name-last-name-check',
                label: this.newNameLastNameLabel,
                setValueMethod: this.setNewNameLastName,
                value: this.newNameLastName
            },
            {
                type: FieldType.CHECKBOX,
                id: 'new-name-first-name-check',
                label: this.newNameFirstNameLabel,
                setValueMethod: this.setNewNameFirstName,
                value: this.newNameFirstName
            },
            {
                type: FieldType.CHECKBOX,
                id: 'new-name-middle-initial-check',
                label: this.newNameMiddleInitialLabel,
                setValueMethod: this.setNewNameMiddleInitial,
                value: this.newNameMiddleInitial
            },
            {
                type: FieldType.CHECKBOX,
                id: 'date-of-rehire-check',
                label: this.rehireDateLabel,
                setValueMethod: this.setRehireDate,
                value: this.rehireDate
            },
            {
                type: FieldType.CHECKBOX,
                id: 'document-title-examined-check',
                label: this.documentTitleExaminedLabel,
                setValueMethod: this.setDocumentTitleExamined,
                value: this.documentTitleExamined
            },
            {
                type: FieldType.CHECKBOX,
                id: 'document-number-examined-check',
                label: this.documentNumberExaminedLabel,
                setValueMethod: this.setDocumentNumberExamined,
                value: this.documentNumberExamined
            },
            {
                type: FieldType.CHECKBOX,
                id: 'expiration-date-examined-check',
                label: this.expirationDateExaminedLabel,
                setValueMethod: this.setExpirationDateExamined,
                value: this.expirationDateExamined
            },
            {
                type: FieldType.CHECKBOX,
                id: 'employee-agree-to-sign-check',
                label: this.agreeToSignLabel,
                setValueMethod: this.setAgreeToSign,
                value: this.agreeToSign
            },
            {
                type: FieldType.DATE,
                id: `todays-date-input`,
                width: 150,
                labelPosition: LabelPosition.LEFT,
                setValueMethod: this.setTodaysDate,
                value: this.todaysDate,
                hasError: this.todaysDateHasError,
                helperText: this.todaysDateInvalidMessage
            }, {
                type: FieldType.TEXT,
                id: `name-of-employer`,
                width: 300,
                labelPosition: LabelPosition.LEFT,
                setValueMethod: this.setEmployerName,
                value: this.representativeNameValue
            }
        ];
    }

    @action.bound
    setDeclineReason(value: DeclineReason) {
        this.declineModalStore.selectReason(value);
    }

    @action.bound
    setDeclineReasonText(value: string) {
        this.declineModalStore.setReasonDescription(value);
    }

    @action.bound
    setNewNameLastName(value: boolean) {
        this.newNameLastName = value;
    }

    @action.bound
    setNewNameFirstName(value: boolean) {
        this.newNameFirstName = value;
    }

    @action.bound
    setNewNameMiddleInitial(value: boolean) {
        this.newNameMiddleInitial = value;
    }

    @action.bound
    setRehireDate(value: boolean) {
        this.rehireDate = value;
    }

    @action.bound
    setDocumentTitleExamined(value: boolean) {
        this.documentTitleExamined = value;
    }

    @action.bound
    setDocumentNumberExamined(value: boolean) {
        this.documentNumberExamined = value;
    }

    @action.bound
    setExpirationDateExamined(value: boolean) {
        this.expirationDateExamined = value;
    }

    @action.bound
    setAgreeToSign(value: boolean) {
        this.agreeToSign = value;
    }

    @action.bound
    setTodaysDate(value: string) {
        this.checkTodaysDate(value);
        this.todaysDate = value;
    }

    @action.bound
    setEmployerName(value: string) {
        this.representativeNameValue = value;
    }

    @action.bound
    setESignAgreement(value: boolean) {
        this.eSignAgreementValue = value;
    }

    @action.bound
    goToReviewStep(): void {
        this.selectedStep = ReviewSection3Step.REVIEW;
    }

    @action.bound
    goToCompletionStep(): void {
        this.selectedStep = ReviewSection3Step.COMPLETION;
    }

    updateWithDecryptedDocumentNumber(
        encryptedDocument: EncryptedDto
    ): string {
        const { personPidKey } = this.configurationValuesService;
        if (!!encryptedDocument?.cipherText && !!encryptedDocument?.iv) {
            return CipherService.decryptAes(
                encryptedDocument.cipherText,
                encryptedDocument.iv,
                personPidKey
            ) || '';
        }

        return '';
    }

    @action.bound
    async getRemoteI9Details(): Promise<void> {
        this.showLoader = true;
        try {
            const response = await this.remoteRepresentativeService.getRemoteI9DetailsAsync();
            const documentTypes = response.documentDetails.map(
              (item) => item.documentType
            );
            const request = { documentTypes, includeDisabled: true };
            const documentTypesValidation =
              await this.remoteRepresentativeService.getDocumentTypes(request);
              this.documentTypesValidationsAll = documentTypesValidation;
            if (!response) {
                console.error('Failed to get Remote I9 Details');
                return;
            }

            this.remoteI9Info = response;

            // map all editable fields

            // checkboxes labels
            this.newNameLastNameLabel = defaultIfEmpty(response.section3Details?.candidateLastName, this.translations.naLabel);
            this.newNameFirstNameLabel = defaultIfEmpty(response.section3Details?.candidateFirstName, this.translations.naLabel);
            this.newNameMiddleInitialLabel = defaultIfEmpty(response.section3Details?.candidateMiddleInitial, this.translations.naLabel);
            this.rehireDateLabel = defaultIfEmpty(response.section3Details?.rehireDate, this.translations.naLabel);
            this.documentTitleExaminedLabel = defaultIfEmpty(response.section3Details?.documentType, this.translations.documentPhysicallyExamined);
            this.documentNumberExaminedLabel = defaultIfEmpty(response.section3Details?.documentNumber, this.translations.documentPhysicallyExamined);
            this.expirationDateExaminedLabel = defaultIfEmpty(response.section3Details?.documentExpireDate, this.translations.documentPhysicallyExamined);
            this.agreeToSignLabel = defaultIfEmpty(response.section3Details?.signatureAgrement, this.translations.agreeToSign);

            // decrypt document Number
            this.remoteI9Info?.documentDetails?.forEach((document, index) => {
                document.documentNumber = this.updateWithDecryptedDocumentNumber(document.number);
            });

        } catch (err) {
            console.error(err);
        } finally {
            this.showLoader = false;
        }
    }

    @action.bound
    async requestESignAsync(): Promise<void> {
        try {
            this.showLoader = true;
            this.hadErrorESigning = false;

            this.eSignIsFilledValue = this.checkESignFilled();
            if (!this.eSignIsFilledValue) {
                return;
            }

            const signI9Request: RepresentativeSection3SignRequest = {
                confirmAgreeToSign: !!this.agreeToSign,
                confirmReadAndAcceptElectronicSignature: !!this.eSignAgreementValue,
                representativeFullName: this.representativeNameValue,
                section3Sign: {
                    confirmDocumentExpiryDate: !!this.expirationDateExamined,
                    confirmDocumentNumber: !!this.documentNumberExamined,
                    confirmDocumentTitle: !!this.documentTitleExamined,
                    confirmEmployeeFirstName: !!this.newNameFirstName,
                    confirmEmployeeLastName: !!this.newNameLastName,
                    confirmEmployeeMiddleInitial: !!this.newNameMiddleInitial,
                    confirmRehireDate: !!this.rehireDate,
                    confirmSignatureAgreement: !!this.agreeToSign
                },
                signedOn: moment().utc().toISOString()
            };

            const response = await this.remoteRepresentativeService.signI9Section3Async(signI9Request);
            if (!response) {
                this.hadErrorESigning = true;
                console.error('Failed to Sign I-9 section 3');
                this.trackingService.trackClickEvent(ClientTrackingCategory.Section3Esign, ClientTrackingName.Error);
                return;
            }

            this.trackingService.trackClickEvent(ClientTrackingCategory.Section3Esign, ClientTrackingName.Success);
            this.goToCompletionStep();

        } catch (error) {
            this.hadErrorESigning = true;
            this.trackingService.trackClickEvent(ClientTrackingCategory.Section3Esign, ClientTrackingName.Error);
            console.error(error);
        } finally {
            this.showLoader = false;
        }
    }

    @action.bound
    async requestDeclineAsync(): Promise<void> {
        try {
            this.showLoader = true;
            this.hadErrorDeclining = false;

            this.declineModalStore.validate();
            if (!this.declineModalStore.wasValid) {
                return;
            }

            const declineI9Request: RepresentativeDeclineRequest = {
                declineReasonText: this.declineModalStore.reasonDescription,
                section3DeclineReason: this.declineModalStore.selectedReason
            };

            const response = await this.remoteRepresentativeService.declineI9Async(declineI9Request);
            if (!response) {
                this.hadErrorDeclining = true;
                console.error('Failed to decline I-9 section 3');
                this.trackingService.trackClickEvent(ClientTrackingCategory.Section3Decline, ClientTrackingName.Error);
                return;
            }

            this.trackingService.trackClickEvent(ClientTrackingCategory.Section3Decline, ClientTrackingName.Success);
            this.closeDeclineModal();
            this.goToCompletionStep();

        } catch (error) {
            this.hadErrorDeclining = true;
            console.error(error);
            this.trackingService.trackClickEvent(ClientTrackingCategory.Section3Decline, ClientTrackingName.Error);
        } finally {
            this.showLoader = false;
        }
    }
}

export default RemoteRepresentativeStore;



// WEBPACK FOOTER //
// ./src/stores/remote-representative/remote-representative-store.ts